
import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

import LanguageSelector from "./LanguageSelector";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { useLocalizedPath } from "@/hooks/useLocalizedPath";
import { useSmartHashNav } from "@/hooks/useSmartHashNav";
import logoCabassol from "@/assets/logo-cabassol.webp";

export default function Navbar() {
  const { t } = useLanguage();
  const { localePath } = useLocalizedPath();
  const { handleHashClick } = useSmartHashNav();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [servicesInView, setServicesInView] = useState(false);
  const [faqInView, setFaqInView] = useState(false);
  
  const navLinks = [
    { name: t.nav.home || "Accueil", path: localePath("/") + "#hero", hash: "hero" },
    { name: t.nav.services || "Services", path: localePath("/services"), hasDropdown: true },
    { name: t.nav.about || "À propos", path: localePath("/about") },
    { name: (t.nav as any).faq || "FAQ", path: localePath("/") + "#faq", hash: "faq" },
    { name: (t.nav as any).reviews || "Avis", path: localePath("/avis") },
    { name: t.nav.blog || "Blog", path: localePath("/blog") },
    { name: t.nav.contact || "Contact", path: localePath("/contact") }
  ];

  const serviceSubLinks = [
    { name: (t.nav as any).allServices || "Tous nos services", path: localePath("/services") },
    { name: (t.nav as any).gestionLocative || "Gestion Locative", path: localePath("/services/gestion-locative") },
    { name: (t.nav as any).conciergerieService || "Conciergerie", path: localePath("/services/conciergerie") },
    { name: (t.nav as any).bailMobiliteService || "Bail mobilité", path: localePath("/services/bail-mobilite-aix-en-provence") },
    { name: (t.nav as any).aLaCarteService || "Prestations à la carte", path: localePath("/services/prestations-a-la-carte") },
    { name: (t.nav as any).staffService || "Personnel de Maison", path: localePath("/services/staff") },
  ];

  const partnerSubLinks = [
    { name: (t.nav as any).yachtCharter || "Yacht Charter", path: localePath("/services/yacht-charter") },
    { name: (t.nav as any).billetterie || "Billetterie", path: localePath("/services/billetterie") },
    { name: (t.nav as any).automobiles || "Automobile", path: localePath("/services/automobiles") },
  ];

  const partnerLabel = (t.nav as any).partnerLabel || "Partenaires";

  const isHomePage = location.pathname === "/" || /^\/[a-z]{2}\/?$/.test(location.pathname);

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 50;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [scrolled]);

  // Track services & FAQ section visibility on homepage
  useEffect(() => {
    if (!isHomePage) {
      setServicesInView(false);
      setFaqInView(false);
      return;
    }

    let observer: IntersectionObserver | null = null;
    let retryTimeout: ReturnType<typeof setTimeout>;

    const setupObserver = () => {
      const servicesEl = document.getElementById("services-section");
      const faqEl = document.getElementById("faq");

      if (!servicesEl && !faqEl) {
        // Elements not yet in DOM, retry
        retryTimeout = setTimeout(setupObserver, 200);
        return;
      }

      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.target.id === "services-section") {
              setServicesInView(entry.isIntersecting);
            } else if (entry.target.id === "faq") {
              setFaqInView(entry.isIntersecting);
            }
          });
        },
        { threshold: 0.15 }
      );

      if (servicesEl) observer.observe(servicesEl);
      if (faqEl) observer.observe(faqEl);
    };

    // Defer to ensure DOM is ready after page render
    retryTimeout = setTimeout(setupObserver, 100);

    return () => {
      clearTimeout(retryTimeout);
      observer?.disconnect();
    };
  }, [isHomePage, location.pathname]);

  const isLinkActive = (link: typeof navLinks[0]) => {
    const currentPath = location.pathname.replace(/\/$/, '') || '/';
    const linkPath = link.path.replace(/#.*$/, '').replace(/\/$/, '') || '/';
    
    if (link.hash === "hero") return isHomePage && !location.hash && !servicesInView && !faqInView;
    if (link.hash === "faq") return (isHomePage && faqInView) || location.hash === "#faq";
    if (link.hasDropdown) {
      if (isHomePage && servicesInView && !faqInView) return true;
      return currentPath.startsWith(linkPath);
    }
    return currentPath === linkPath;
  };

  const renderNavLink = (link: typeof navLinks[0], isMobile = false) => {
    const active = isLinkActive(link);
    const baseClass = isMobile
      ? cn(
          "text-2xl font-display transition-colors",
          active ? "text-gold" : "text-foreground hover:text-muted-foreground"
        )
      : cn(
          "text-sm font-medium tracking-wider uppercase transition-all duration-300 relative inline-flex items-center gap-1 whitespace-nowrap",
          active
            ? "text-gold"
            : scrolled || !isHomePage
              ? "text-foreground/70 hover:text-foreground"
              : "text-white/80 hover:text-white"
        );

    if (link.hash) {
      return (
        <a
          href={link.path}
          className={baseClass}
          onClick={(e) => handleHashClick(e, link.hash!, isMobile ? () => setMobileMenuOpen(false) : undefined)}
        >
          {link.name}
        </a>
      );
    }

    return (
      <Link
        to={link.path}
        className={baseClass}
        onClick={isMobile ? () => setMobileMenuOpen(false) : undefined}
      >
        {link.name}
        {!isMobile && link.hasDropdown && (
          <ChevronDown className="w-3 h-3 transition-transform duration-300 group-hover:rotate-180" />
        )}
        {!isMobile && <span className={cn("absolute -bottom-1 left-0 h-px bg-current transition-all duration-500", active ? "w-full bg-gold" : "w-0 group-hover:w-full")} aria-hidden="true" />}
      </Link>
    );
  };
  
  return (
    <header 
      className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-700 ease-elegant",
        scrolled 
          ? "bg-background/95 backdrop-blur-md py-4 border-b border-border" 
          : isHomePage 
            ? "bg-transparent py-6" 
            : "bg-background py-6 border-b border-border"
      )}
    >
      <nav className="container flex justify-between items-center">
        {/* Logo */}
        <Link 
          to={localePath("/")} 
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="group flex items-center gap-0"
        >
          <img 
            src={logoCabassol} 
            alt="Conciergerie Cabassol" 
            width="40"
            height="40"
            fetchPriority="high"
            decoding="async"
            className="block h-10 w-10 lg:h-14 lg:w-14 xl:h-16 xl:w-16 translate-y-0.5 md:translate-y-1 transition-all duration-500"
          />
          <div className="flex flex-col justify-center">
            <span 
              className={cn(
                "text-base lg:text-xl xl:text-2xl font-display font-normal tracking-wide leading-snug transition-colors duration-500",
                scrolled || !isHomePage ? "text-foreground" : "text-white"
              )}
            >
              Conciergerie
            </span>
            <span 
              className={cn(
                "text-base lg:text-xl xl:text-2xl font-display font-normal tracking-wide leading-snug transition-colors duration-500 -mt-0.5 lg:-mt-1",
                scrolled || !isHomePage ? "text-foreground" : "text-white"
              )}
            >
              Cabassol
            </span>
          </div>
        </Link>

        {/* Desktop Navigation */}
        <nav aria-label="Navigation principale" className="hidden lg:block ml-16 xl:ml-20">
          <ul className="flex items-center space-x-4 xl:space-x-8">
            {navLinks.map(link => (
              <li key={link.name} className={link.hasDropdown ? "relative group" : ""}>
                {renderNavLink(link)}
                {link.hasDropdown && (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 translate-y-1 group-hover:translate-y-0 z-[60]">
                    <ul className="bg-background border border-border shadow-lg rounded-sm min-w-[220px] py-2">
                      {serviceSubLinks.map((subLink, i) => (
                        <li key={subLink.path}>
                          <Link
                            to={subLink.path}
                            className="block px-5 py-2.5 text-sm text-foreground/70 hover:text-foreground hover:bg-muted/50 transition-colors duration-200"
                          >
                            {subLink.name}
                          </Link>
                          {i === 0 && <div className="mx-4 h-px bg-border my-1" />}
                        </li>
                      ))}
                      {/* Partner separator */}
                      <li className="mx-4 mt-1 mb-1">
                        <div className="h-px bg-border mb-2" />
                        <span className="text-[10px] text-muted-foreground/60 uppercase tracking-widest px-1">{partnerLabel}</span>
                      </li>
                      {partnerSubLinks.map((subLink) => (
                        <li key={subLink.path}>
                          <Link
                            to={subLink.path}
                            className="block px-5 py-2.5 text-sm text-foreground/70 hover:text-foreground hover:bg-muted/50 transition-colors duration-200"
                          >
                            {subLink.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </nav>

        <div className="hidden lg:flex items-center space-x-4">
          <LanguageSelector />
          <Button 
            asChild 
            variant="hero"
            className="px-5 py-2.5 text-xs tracking-widest uppercase font-medium"
          >
            <a href={localePath("/") + "?profile=owner#formulaire"}>Estimation Locative</a>
          </Button>
        </div>

        {/* Mobile Menu Button */}
        <div className="lg:hidden flex items-center space-x-3">
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)} 
            className={cn(
              "p-2 transition-colors",
              scrolled || !isHomePage ? "text-foreground" : "text-white"
            )}
            aria-label={mobileMenuOpen ? "Fermer le menu" : "Ouvrir le menu"}
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-menu"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" aria-hidden="true" /> : <Menu className="h-6 w-6" aria-hidden="true" />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[200] lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Mobile Menu Panel */}
      <aside 
        id="mobile-menu"
        className={cn(
          "fixed top-0 right-0 w-[85vw] max-w-sm h-dvh bg-background border-l border-border shadow-2xl z-[201] lg:hidden transition-transform duration-500 ease-elegant",
          mobileMenuOpen ? "translate-x-0" : "translate-x-full"
        )}
        role="dialog"
        aria-label="Menu de navigation mobile"
        aria-hidden={!mobileMenuOpen}
      >
        <div className="flex flex-col h-full p-8">
          {/* Close button */}
          <div className="flex justify-between items-center mb-12">
            <span className="text-xl font-display">Menu</span>
            <button 
              onClick={() => setMobileMenuOpen(false)}
              className="p-2"
              aria-label="Fermer le menu"
            >
              <X className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
          
          {/* Navigation Links */}
          <nav aria-label="Navigation mobile">
            <ul className="space-y-6">
              {navLinks.map((link, index) => (
                <li 
                  key={link.name}
                  className={cn(
                    "transition-all duration-300",
                    mobileMenuOpen ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"
                  )}
                  style={{ transitionDelay: mobileMenuOpen ? `${index * 80}ms` : '0ms' }}
                >
                  {link.hasDropdown ? (
                    <div>
                      <Link 
                        to={link.path}
                        className="text-2xl font-display text-foreground hover:text-muted-foreground transition-colors"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {link.name}
                      </Link>
                      <ul className="mt-3 ml-4 space-y-2 border-l border-border pl-4">
                        {serviceSubLinks.filter((_, i) => i > 0).map(subLink => (
                          <li key={subLink.path}>
                            <Link
                              to={subLink.path}
                              className="text-base text-muted-foreground hover:text-foreground transition-colors"
                              onClick={() => setMobileMenuOpen(false)}
                            >
                              {subLink.name}
                            </Link>
                          </li>
                        ))}
                        <li className="pt-1">
                          <span className="text-[10px] text-muted-foreground/60 uppercase tracking-widest">{partnerLabel}</span>
                        </li>
                        {partnerSubLinks.map(subLink => (
                          <li key={subLink.path}>
                            <Link
                              to={subLink.path}
                              className="text-base text-muted-foreground hover:text-foreground transition-colors"
                              onClick={() => setMobileMenuOpen(false)}
                            >
                              {subLink.name}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    renderNavLink(link, true)
                  )}
                </li>
              ))}
            </ul>
          </nav>
          
          {/* Language Selector */}
          <div className="mt-auto pt-8 border-t border-border">
            <LanguageSelector />
          </div>
        </div>
      </aside>
    </header>
  );
}
